import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SlidePanel from '../shared/SlidePanel';
import useSites from '../multiLocation/useSites';
import { siteColour } from '../../_services/sites.service';
import { fetchRoles, fetchPeople, setAssignment, assignableRoles, sourceLabel, errorText } from './rolesStore';
import { getAccess } from './access';
import '../settings/SettingsModern.css';
import '../multiLocation/multiLocation.css';
import './permissions.css';

/**
 * "Access" on a team member's profile: their role here and (multi-location,
 * owner only) the other locations it applies at. Roles themselves are defined
 * under Team > Permissions; this only assigns one. The server enforces who
 * may change whom (docs/permissions.md §5) — this hides what it knows you
 * can't do.
 */
export default function AccessSection({ userId, firstName, employmentTypeId, isProfileOwner, isOwnProfile }) {
  const { sites, multi } = useSites();
  const [data, setData] = useState({ loading: true, error: null, roles: [], person: null, canManage: false, grantable: [], ownerOnly: [] });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, people] = await Promise.all([fetchRoles(), fetchPeople()]);
      const person = people.find((p) => String(p.userId) === String(userId)) || null;
      setData({ loading: false, error: null, roles: r.roles, person, canManage: r.canManage, grantable: r.grantable, ownerOnly: r.ownerOnly });
    } catch (e) {
      setData((d) => ({ ...d, loading: false, error: errorText(e, 'Could not load access.') }));
    }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  if (data.loading) return <div className="acc-section"><div className="acc-head"><h3>Access</h3></div><p className="acc-note">Loading…</p></div>;
  if (data.error) return <div className="acc-section"><div className="acc-head"><h3>Access</h3></div><p className="acc-note">{data.error}</p></div>;
  const person = data.person;
  if (!person) return null;
  const role = data.roles.find((r) => r.key === person.roleKey) || null;
  if (!role) return null;

  const here = String(localStorage.getItem('salonId') || '');
  const mine = new Set(data.grantable);
  const outranksMe = !role.owner && role.caps.some((c) => !mine.has(c));
  const editable = data.canManage && !isOwnProfile && !isProfileOwner && !role.owner && !outranksMe;
  const tag = sourceLabel(person.source);
  const elsewhere = sites.filter((s) => (person.salonIds || []).includes(String(s.salonId)));

  return (
    <div className="acc-section">
      <div className="acc-head">
        <h3>Access</h3>
        {editable && <button type="button" className="acc-edit" onClick={() => setOpen(true)}>Edit</button>}
      </div>
      <div className="es-info-list">
        <div className="es-info-item">
          <span className="es-info-label">Role</span>
          <span className="es-info-value">{role.name}{tag && person.source === 'default' && <span className="settings-badge settings-badge-default acc-badge">{tag}</span>}</span>
        </div>
        {multi && !role.owner && (
          <div className="es-info-item">
            <span className="es-info-label">Also applies at</span>
            <span className="es-info-value">{elsewhere.length ? elsewhere.map((s) => s.salonName).join(', ') : 'Only this location'}</span>
          </div>
        )}
        {multi && role.owner && <div className="es-info-item"><span className="es-info-label">Applies at</span><span className="es-info-value">Every location</span></div>}
      </div>
      <p className="acc-note">
        {role.description}{' '}
        {person.source === 'default' && !role.owner && <>They're on the default for {Number(employmentTypeId) === 2 ? 'self-employed stylists' : 'employed team members'}. </>}
        <Link className="acc-link" to="/staff/permissions">What each role can do</Link>
      </p>
      {data.canManage && !isOwnProfile && outranksMe && <p className="acc-note">Their role can do things yours can't, so only the owner can change it.</p>}
      {open && (
        <AccessPanel
          userId={userId}
          firstName={firstName}
          employmentTypeId={employmentTypeId}
          person={person}
          roles={assignableRoles(data.roles)}
          grantable={mine}
          sites={sites}
          multi={multi}
          here={here}
          onClose={() => setOpen(false)}
          onSaved={async () => { setOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

function AccessPanel({ userId, firstName, employmentTypeId, person, roles, grantable, sites, multi, here, onClose, onSaved }) {
  const isOwner = !!getAccess().owner;
  const [roleKey, setRoleKey] = useState(person.roleKey);
  const [salonIds, setSalonIds] = useState((person.salonIds || []).map(String));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const role = roles.find((r) => r.key === roleKey) || roles[0];
  const se = Number(employmentTypeId) === 2;
  // Access at other locations is the owner's to give (server rule).
  const canPickWhere = multi && isOwner;
  const others = sites.filter((s) => String(s.salonId) !== here);
  const worksAt = new Set((person.worksAt || []).map(String));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await setAssignment(userId, canPickWhere ? { roleKey, salonIds } : { roleKey });
      await onSaved();
    } catch (e) {
      setError(errorText(e, 'Could not save. Please try again.'));
    } finally { setBusy(false); }
  };

  const hereSite = sites.find((s) => String(s.salonId) === here);
  const names = [hereSite ? hereSite.salonName : 'this location'].concat(canPickWhere ? others.filter((s) => salonIds.includes(String(s.salonId))).map((s) => s.salonName) : []);
  const whereText = !multi ? '' : names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

  return (
    <SlidePanel title={`${firstName ? `${firstName}'s` : 'Their'} access`} onClose={onClose} width={480} className="ml-panel"
      footer={<>
        <button type="button" className="sp-btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="sp-btn-primary" onClick={save} disabled={busy || !role}>{busy ? 'Saving…' : 'Save'}</button>
      </>}>
      <div className="settings-form-group">
        <label className="settings-label">Role</label>
        <div className="bu-modes acc-modes">
          {roles.map((r) => {
            const beyond = r.caps.some((c) => !grantable.has(c));
            return (
              <button key={r.key} type="button" className={`bu-mode${roleKey === r.key ? ' is-active' : ''}${beyond ? ' is-disabled' : ''}`} onClick={() => !beyond && setRoleKey(r.key)} aria-pressed={roleKey === r.key} aria-disabled={beyond}>
                <span className="bu-mode-radio" aria-hidden />
                <span className="bu-mode-text">
                  <span className="bu-mode-title">{r.name}</span>
                  <span className="bu-mode-desc">{beyond ? 'Can do things you can\'t, so only the owner can give it.' : r.description}</span>
                </span>
              </button>
            );
          })}
        </div>
        {se && <p className="acc-note">A role doesn't change their pay, commission or who owns their clients. Their own bookings, clients and money stay theirs, and marketing and loyalty for their clients stay their call.</p>}
        <p className="acc-note">Owner belongs to the account holder and can't be given to anyone else. <Link className="acc-link" to="/staff/permissions">Change what a role can do</Link></p>
      </div>

      {canPickWhere && others.length > 0 && (
        <div className="settings-form-group">
          <label className="settings-label">Also applies at</label>
          <div className="ml-panel-list ml-sites-pick" role="group">
            {[hereSite].filter(Boolean).concat(others).map((s) => {
              const id = String(s.salonId);
              const isHere = id === here;
              const on = isHere || salonIds.includes(id);
              return (
                <button key={id} type="button" className={`ml-site-pick${on ? ' is-on' : ''}`} role="checkbox" aria-checked={on} disabled={isHere} onClick={() => setSalonIds(on ? salonIds.filter((x) => x !== id) : salonIds.concat(id))}>
                  <span className="ml-dot" style={{ background: siteColour(s.salonId) }} />
                  <span className="ml-site-pick-text">
                    <span className="ml-site-pick-name">{s.salonName}{isHere && <span className="settings-badge settings-badge-info" style={{ marginLeft: 8 }}>This location</span>}{!isHere && worksAt.has(id) && <span className="settings-badge settings-badge-default" style={{ marginLeft: 8 }}>Works here</span>}</span>
                    {s.formattedAddress && <span className="ml-site-pick-sub">{s.formattedAddress}</span>}
                  </span>
                  <span className="ml-site-pick-box" aria-hidden="true">
                    {on && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="acc-note">Where they work, ticking gives them the same role there; unticking puts them back on their default. Where they don't work, ticking lets them sign in there with this role. Their diary, bookings and pay stay where they work.</p>
        </div>
      )}

      {role && (
        <div className="acc-summary">
          <span className="acc-summary-tick"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg></span>
          <span>{firstName || 'They'} will have <b>{role.name}</b> access{whereText ? <> at {whereText}</> : null}.</span>
        </div>
      )}
      {error && <p className="acc-note perm-error">{error}</p>}
    </SlidePanel>
  );
}
